//  eslint-disable sonar/no-nested-conditional 
import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Colors, FontSizes, MediaQueries, Spacing } from "@paclife/plds.tokens";

import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import Card from "common/Card/Card";
import Typography from "common/Typography/Typography";
import titleCase from "utils/formatters/titleCase"
import Button from "common/Button/Button";
import { Icon } from "@paclife/plds.icon";
import Loading from "common/Loading/Loading";
import { useTeamContext } from "pages/MyTeam/MyTeam";
import { revokeTeamMember, reinviteTeamMember, removeFromTeam } from "api/team"; 
import ErrorMessage from "common/Message/Message";

const Text = styled(Typography)`
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &.Invited {
        color: ${Colors.primary};
    }
`;

const Grid = styled.div`
    width: 100%;
    margin-bottom: 12px;
    display: grid;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    @media (min-width: ${MediaQueries.md}) {
        grid-template-columns: 1.5fr 2.5fr 1fr 1fr 1.5fr .5fr .5fr;
    }
    @media (min-width: ${MediaQueries.lg}) {
        grid-template-columns: 1.5fr 2.5fr 1fr 1fr 2.0fr 1fr 1fr 20px;
    }
`;

const MemberGrid = styled.div`
    width: 100%;
    margin-bottom: 12px;
    display: grid;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    @media (min-width: ${MediaQueries.md}) {
        grid-template-columns: 2.0fr 2.5fr .5fr;
    }
    @media (min-width: ${MediaQueries.lg}) {
        grid-template-columns: 2.0fr 2.5fr .5fr 20px;
    }
`;

const RowHeader = styled(Grid)`
    margin-top: 20px;
    border-bottom: 1px solid ${Colors.neutral800};
`;

const Row = styled(Grid)`
    border-bottom: 1px solid ${Colors.neutral200};
`;

const MemberRowHeader = styled(MemberGrid)`
    border-bottom: 1px solid ${Colors.neutral800};
`;

const MemberRow = styled(MemberGrid)`
    border-bottom: 1px solid ${Colors.neutral200};
`;

const NewInvite = styled.div`
    float: right;
    clear: none;
    position: relative;
    top: -44px;
    a {
        font-size: 1.10rem;
        font-weight: 600;
    }
`;

const TrashBin = styled(Icon)`
    width: 22px;
    cursor: pointer;
`;

const Resend = styled(Icon)`
    width: 22px;
    cursor: pointer;
`;

const Add = styled(Icon)`
    width: 22px;
    margin-right: 0.9rem;
`;

const Notice = styled(Typography)`
    margin-bottom: ${Spacing[16]};
    font-size: ${FontSizes[400]};
    line-height: 1rem;
`;

const LinkStyles = css`
    :hover {
        text-decoration: none;
    }
    color: ${Colors.neutral0};
    font-size: ${FontSizes[600]};
    display: block;
    padding: 6px 6px;
`;

const ScrollToLink = styled.a`
    ${LinkStyles}
    color: ${Colors.blue600};
`;

const DISPLAY_FIELDS = [
    { label: "Name", name: "name" },
    { label: "Email", name: "emailAddress" },
    { label: "Role", name: "appointed" },
    { label: "Status", name: "status" },
    { label: "Last Active", name: "lastLoginDateTime" },
    { label: "Resend", name: "resend" },
    { label: "Remove", name: "revoke" }

];

const MEMBERSHIP_FIELDS = [
    { label: "Name", name: "name" },
    { label: "Email", name: "emailAddress" },
    { label: "Remove", name: "remove" }
];

const REVOKE_MODAL = "revoke";
const REMOVE_MODAL = "remove";
const RESEND_MODAL = "resend";

const RESEND_ALLOWED = new Set(["Expired", "Invited"]);


const ManageMyTeam = () => {
    const { t } = useTranslation("manMyTeam");
    const {
        team,
        setTeam,
        membership,
        setMembership,
        loading,
        loading: teamLoading,
        error: initError
    } = useTeamContext();
    const [mobileView, setMobileView] = useState(false);
    const [modal, setModal] = useState(false);
    const [memberId, setMemberId] = useState("");
    const [email, setEmail] = useState("");
    const [error, setError] = useState(false);
    const [manageTeamDisplay, setManageTeamDisplay] = useState(false);
    const [teamMemberDisplay, setTeamMemberDisplay] = useState(false);
    const { rsdUpReadonlyaccessEn } = useFlags();

    useEffect(() => {
        function updateView() {
            if (window.innerWidth <= parseInt(MediaQueries.md)) {
                setMobileView(true);
            } else {
                setMobileView(false);
            }
        }
        window.addEventListener("resize", updateView);
        updateView();
        return () => window.removeEventListener("resize", updateView);
    }, []);


    useEffect(() => {
        team.length > 0 &&
            setManageTeamDisplay(true);
    }, [team]);

    useEffect(() => {
        membership.length > 0 &&
            setTeamMemberDisplay(true);
    }, [membership]);


    const openModal = (modal, memberId, email) => {
        setModal(modal);
        setMemberId(memberId);
        setEmail(email);
    };

    const closeModal = () => {
        setModal(false);
        setMemberId("");
        setEmail("");
        setError("");
    };

    const handleClick = async (e) => {
        if (rsdUpReadonlyaccessEn) return;
        if (modal === REVOKE_MODAL) {
            handleRevoke(e);
        } else if (modal === REMOVE_MODAL) {
            handleRemove(e);
        } else {
            handleResend(e);
        }
    };

    const handleRevoke = async (e) => {
        const { succeed, message } = await revokeTeamMember(memberId);

        if (succeed) {
            setTeam((prev) => {
                return prev.filter((member) => member.id !== memberId);
            });
            closeModal();
        } else {
            setError(message);
        }
    };

    const handleRemove = async (e) => {
        const { succeed, message } = await removeFromTeam(memberId);

        if (succeed) {
            setMembership((prev) => {
                return prev.filter((member) => member.id !== memberId);
            });
            closeModal();
        } else {
            setError(message);
        }
    };

    const handleResend = async (e) => {
        const { succeed, message } = await reinviteTeamMember(memberId);

        if (succeed) {
            closeModal();
        } else {
            setError(message);
        }
    };

    const teamTools = (fieldNm, memId, memEmail, memStatus) => {
        if (fieldNm === 'revoke') {
            return (
                <Text>
                    <div onClick={() =>
                            openModal(
                                REVOKE_MODAL,
                                memId,
                                memEmail
                            )
                        }
                        disabled={rsdUpReadonlyaccessEn}
                        data-track="analytics"
                        data-bname="revoke-access"
                        title="Remove Access"
                    >
                        <TrashBin 
                            name="delete" 
                            color={Colors.red700}
                        />
                    </div>
                </Text>
            )
        }

        if (fieldNm === 'resend' && 
            RESEND_ALLOWED.has(memStatus)) {
                return (
                    <>
                        {RESEND_ALLOWED.has(memStatus) && 
                            <div onClick={() =>
                                openModal(
                                    RESEND_MODAL,
                                    memId,
                                    memEmail
                                )
                            }
                                data-track="analytics"
                                data-bname="resend-invite"
                                disabled={rsdUpReadonlyaccessEn}
                                title="Resend Access"
                            >
                                <Resend 
                                    name="redo" 
                                    color="#333"
                                />
                            </div>
                        }
                    </>
                )
            }       
    }

    const memberTools = (mId, mEmail) => {
        return (
            <div onClick={() =>
                openModal(
                    REMOVE_MODAL,
                    mId,
                    mEmail
                )
            }
                data-track="analytics"
                data-bname="remove-access"
                disabled={rsdUpReadonlyaccessEn}
                title="Remove Access"
            >
                <TrashBin 
                    name="delete" 
                    color="#C30000"
                />
            </div>
        )  
    }

    const loadGridHead = (team) => {
        const ret = <Text variant="body">Loading...</Text>
        if (initError !== "") {
            if (initError === "No Membership record found.") {
                setManageTeamDisplay(false);
            } else {
                return (`${ret}${<ErrorMessage message={initError} type="error" />}`);
            }
        }
    }

    const formatTbleData = (mFieldName, mValue) => {
        if (mFieldName === "appointed") {
            if (mValue) {
                return 'Appointed';
            } else {
                return 'Assistant';
            }
        } else {
            return titleCase(mValue);
        }
    } 

    return (
        <>
            { loading && ( <Loading /> ) }
            { manageTeamDisplay &&
                <Card title={t("manage.title")}>
                    <NewInvite>
                        <ScrollToLink href='#invite'>                                        
                            <Add name="add" color={Colors.blue600} />
                            {t("invite.title")}
                        </ScrollToLink>
                    </NewInvite>

                    <Notice>
                        {t("manage.message")}
                    </Notice>
                
                    {!mobileView && (
                        <RowHeader>
                            {DISPLAY_FIELDS.map((field) => (
                                <Text key={field.label} variant="caption">
                                    {field.label}
                                </Text>
                            ))}
                        </RowHeader>
                    )}
                    {/* Manage My Team */}
                    {teamLoading ? (
                            loadGridHead(team)
                        ) : (
                            team.map((member, index) => (
                            <Row key={index}>
                                {DISPLAY_FIELDS.map((field) => (
                                    <Text key={index}
                                        variant="body"
                                        className={member[field.name]}
                                    >
                                        {mobileView 
                                            ? `${field.label}: ${
                                                formatTbleData(field.name, member[field.name]) || "-"
                                                }`     
                                            : formatTbleData(field.name, member[field.name]) ||      
                                                    teamTools(
                                                        field.name,
                                                        member.id,
                                                        member.emailAddress,
                                                        member.status
                                                    )
                                        }
                                    </Text>
                                ))}
                            </Row>
                        ))
                    )}
                </Card>
            } 
        
            { teamMemberDisplay && 
                <Card title={t(`membership.title`)}>
                    <Notice>
                        {t("membership.message")}
                    </Notice>
                    {!mobileView && (
                        <MemberRowHeader>
                            {MEMBERSHIP_FIELDS.map((field) => (
                                <Text key={field.label} variant="caption" title={field.label}>
                                    {field.label}
                                </Text>
                            ))}
                        </MemberRowHeader>
                    )}
            
                    {( membership.map((member, index) => (
                            <MemberRow key={index}>
                                {MEMBERSHIP_FIELDS.map((mField) => (
                                    <Text key={index}
                                        variant="body"
                                        className={member[mField.name]}
                                    >
                                        {mobileView && member[mField.name] !== undefined
                                            ? `${mField.label}: ${
                                                titleCase(member[mField.name]) || "-"
                                            }`
                                            : titleCase(member[mField.name]) || 
                                                memberTools(
                                                    member.id,
                                                    member.emailAddress
                                                )
                                        }
                                    </Text>
                                ))}
                            </MemberRow>
                        ))
                    )}
                </Card>
            }

            { modal && (
                <ConfirmModal
                    disableOutsideClick
                    title={t(`manage.${modal}.title`)}
                    subtitle={t(`manage.${modal}.subtitle`, { value: email })}
                    closeModal={closeModal}
                    primaryButton={
                        <Button
                            variant="primary"
                            onClick={handleClick}
                            data-track="analytics"
                            data-bname={`confirm-${modal}`}
                            disabled={rsdUpReadonlyaccessEn}
                        >
                            {t(`manage.${modal}.primaryButton`)}
                        </Button>
                    }
                    secondaryButton={
                        <Button
                            variant="secondaryNeutral"
                            onClick={closeModal}
                            data-track="analytics"
                            data-bname={`cancel-${modal}`}
                        >
                            {t(`manage.${modal}.secondaryButton`)}
                        </Button>
                    }
                    error={error}
                    loading={loading}
                />
            )}
        </>
    );
};

export default ManageMyTeam;
